var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from "lodash";
import { RooDate, RooDateTime } from "roo-time";
import { getNextPaybackReminderCardDate } from "./incomeSplitterTools.js";
import { narrowingListIncludes, narrowingStringIncludes, objectTypeHasInfix, objectTypeHasPrefix, objectTypeHasSuffix, roundTo3Decimals, } from "./sharedUniversalTools.js";
import { HOURS_TO_WAIT_BEFORE_PROCESSING_DELETE, } from "./models/cardSorter.models.js";
import { s_log } from "./services/log/index.js";
/* Utilities that are specific to the roo domain (for non-roo-specific utils, see sharedUniversalTools.ts). */
export function getPouchName(p) {
    if (p.pouch.type === "CUSTOM")
        return p.pouch.name;
    return getFixedPouchName({ pouchType: p.pouch.type, i18N: p.i18N });
}
export function getFixedPouchName(p) {
    switch (p.pouchType) {
        case "BILLS":
            return p.i18N.common.bills;
        case "LIVING":
            return p.i18N.common.living;
        case "RESERVOIR":
            return p.i18N.common.reservoir;
        case "PAYBACK":
            return p.i18N.common.payback;
    }
}
export function getAccountName(p) {
    var _a;
    return (_a = p.account.nickname) !== null && _a !== void 0 ? _a : p.account.c_name;
}
export function getAccountNameWithLast4(p) {
    // Aggregators occasionally return a missing/blank mask (Plaid/Quiltt fall back to "" — see plaidUtils.ts
    // and quiltt.ts). Render just the name in that case rather than a dangling "*".
    const digits = p.account.c_account_last_4_digits;
    return digits ? `${getAccountName(p)} *${digits}` : getAccountName(p);
}
/**
 * Note: These are just the reasons that don't depend on Account.type and BudgetAccount.type.
 * There are additional reasons beyond these why an account state transition may be forbidden;
 * see also T.R.isSupportedAccountTypesForState() and AcctStateTransitionSupplementalInfo.
 */
export function getTypeIndependentReasonsCantAddToBudget(account) {
    const reasonsCantAddToBudget = [];
    if (account.hidden)
        reasonsCantAddToBudget.push("accountHidden");
    if (account.c_closed)
        reasonsCantAddToBudget.push("accountClosed");
    if (account.removed_at)
        reasonsCantAddToBudget.push("accountRemoved");
    if (account.Membership.archived_at)
        reasonsCantAddToBudget.push("membershipArchived");
    if (account.c_currency_code !== "USD")
        reasonsCantAddToBudget.push("unsupportedCurrency");
    return reasonsCantAddToBudget;
}
// ON_BUDGET is for cash-like accounts AND credit cards. A credit card DEFAULTS to PAYBACK_MODE (carried
// debt paid down via the Payback pouch), but a user can set it ON_BUDGET to treat it like cash — its
// charges become this-month spending. A LOAN is never on-budget. See mobile CLAUDE.md § Payback.
//
// ⚠ VENMOPAYPAL is cash-like but deliberately NOT on this list (T-321, business direction). A wallet
// account is a PROXY, not a source of funds: it spends money it pulls from a linked bank or card at
// payment time, so on-budget it books the same dollar twice — once in the bank feed, once in the
// wallet — leaving the user to reconcile a transfer pair per purchase (the shape `transferReconciler`
// already works around). Its balance/transaction feed is also unreliable enough that on-budget math
// drifts from bank truth. Both reasons apply to PayPal exactly as they do to Venmo, which is why the
// rule sits on the shared type rather than on the institution.
//
// ⚠ This governs TRANSITIONS, not existing rows. ~10 prod accounts were ON_BUDGET before this rule and
// are deliberately grandfathered: leaving ON_BUDGET mints a FINAL_ACCOUNT_ARCHIVE entry that REQUIRES
// user-chosen balanceDestinations, so there is no correct server-side migration. Same grandfathering
// shape as the pre-2026-02-23 ON_BUDGET INVESTMENT/UNKNOWN accounts documented on
// `accountTracksTxnEntries`.
//
// ⚠ CONVERGENCE IS USER-DRIVEN AND OPTIONAL. The only mechanism is the Account Settings nudge
// (`AccountSettings.comp.tsx`), which offers the move but never makes it — nothing converges these rows
// automatically, so a grandfathered row must be read as a PERMANENT state, not a transient one awaiting
// cleanup. The accepted cost is audit replay refusing (r-Kx9pQ2vT) on those budgets, because its guards
// defer to this matrix as the single source of truth.
//
// ⚠ The matrix is checked on TRANSITIONS ONLY, and a same-state re-write is deliberately exempt on both
// sides — mobile's `getAccountStateHardFailReason` and the server's `_setStateCore`. A start-date edit
// re-writes the current state to carry a `txnCutoffDateOverride`; without the exemption it would fail
// the matrix and lock a grandfathered account out of its own start date. Move the two together.
//
// ⚠ CONSEQUENCE FOR UI: a picker or table filtered by `isSupportedAccountTypesForState` must still
// include an account's CURRENT state, or it will fail to represent a grandfathered account at all.
// See `AccountSettings.comp.tsx` — both its state Select and `AccountBehaviorTable` carry that carve-out.
export const ACCOUNT_TYPES_SUPPORTED_FOR_ON_BUDGET = [
    "CHECKING",
    "SAVINGS",
    // CASH is a behavioral clone of CHECKING (cash-like, day-to-day, ON_BUDGET-capable). Manual-only —
    // never produced by an aggregator; `s_account.setType` blocks retyping a synced account to CASH.
    "CASH",
    "CREDIT_CARD",
];
// PAYBACK absorbs both card and loan carried-balance paydown (the account type carries card-vs-loan).
export const ACCOUNT_TYPES_SUPPORTED_FOR_PAYBACK = ["CREDIT_CARD", "LOAN"];
/**
 * The "contributing" / on-budget-like account states (today: just ON_BUDGET). Use this anywhere a
 * check means "this is the on-budget (vs PAYBACK/debt) side" of a transfer/match/processing decision.
 */
export function isContributingAccountState(state) {
    return state === "ON_BUDGET";
}
/**
 * Can money arriving in this account be INCOME? No for a `PAYBACK_MODE` account: it is a LIABILITY
 * (a card or a loan — not just a credit card), so money arriving there pays debt down rather than
 * entering the budget. Nothing the aggregator says can make a paycheck land on a credit card.
 *
 * ⚠ Two deliberate departures elsewhere, neither a bug: cashflow reporting also refuses income on a
 * card or loan by TYPE, because an account outside the budget still has no earnings to report
 * (roo-server `cashflowClassification.ts`); and an explicit `FINAL_TXN_PAYBACK_INCOME` sort DOES read
 * as income there, because a user's own statement about their row outranks a derivation.
 *
 * ⚠ **This is the ONE definition of that rule — three gates consume it** (T-377): the classifier
 * override in `giveRecommendationForCard`, the income QUESTION in `resolveNormalCardRecommendation`,
 * and the Split-Income affordances in `planNormalCardActions`. They were three hand-written
 * comparisons in two packages, agreeing only by coincidence — one of them carried a sign term the
 * others didn't. Suppressing income in one place demonstrably relocates the prompt rather than
 * removing it, so "three gates, one rule" has to be true by construction, not by comment.
 *
 * ⚠ An UNKNOWN state fails **open** (income stays possible), matching every caller's pre-T-377
 * behavior — an account we failed to resolve must not silently lose its income affordances.
 *
 * Distinct from `isContributingAccountState`: that asks "is this the on-budget side of a transfer",
 * which `OFF_BUDGET` also answers no to. This asks only about the liability case.
 */
export function accountCanReceiveIncome(p) {
    return p.state !== "PAYBACK_MODE";
}
/**
 * The balance to DISPLAY for an account (account rows, account detail, running-balance anchor).
 * Net worth doesn't call this — it's a server series (`getNetWorthHistory`) whose past points are
 * bank-reported snapshots. From today onward it uses balance_total for ON_BUDGET accounts, matching
 * this helper, so the Net Worth headline agrees with the account rows (T-417).
 *   - ON_BUDGET → balance_total — Roo's sum of ALL its transactions (incl. unprocessed/pending)
 *   - PAYBACK / OFF_BUDGET → c_balance — bank-reported
 */
export function getDisplayedAccountBalance(account) {
    return displaysDerivedBalance(account) ? account.balance_total : account.c_balance;
}
/**
 * Is the balance Roo DISPLAYS for this account its own derived `balance_total` (true), or the bank's
 * `c_balance` (false)? The one rule `getDisplayedAccountBalance` switches on — anything reasoning about
 * "Roo's number vs the bank's" (e.g. the mobile Ignore confirm's drift warning, T-459) keys on this too,
 * so the two can't disagree about which accounts can drift from their bank. Exhaustive on purpose: a new
 * account state must decide.
 */
export function displaysDerivedBalance(account) {
    switch (account.state) {
        case "ON_BUDGET":
            return true;
        case "PAYBACK_MODE":
        case "OFF_BUDGET":
            return false;
    }
}
export function isSupportedAccountTypesForState(state, type) {
    switch (state) {
        case "ON_BUDGET":
            return ACCOUNT_TYPES_SUPPORTED_FOR_ON_BUDGET.includes(type);
        case "PAYBACK_MODE":
            return ACCOUNT_TYPES_SUPPORTED_FOR_PAYBACK.includes(type);
        case "OFF_BUDGET":
            return true;
    }
}
/**
 * Do this account's Transactions get an Entry — the budget's record of how a transaction was HANDLED
 * (sorted into a pouch, matched, ignored, awaiting post)?
 *
 * A LOAN never does. Its money moves through the checking account and the Payback pouch, never through
 * the loan itself, so there is nothing for an Entry to record; its balance is `c_balance` (bank truth),
 * and `updateDerivedBalances` doesn't even compute `balance_total` for a non-ON_BUDGET account. A LOAN in
 * PAYBACK_MODE is, for entry purposes, exactly as off-budget as one that isn't. This restates the rule
 * already written on `account_type.LOAN` in schema.prisma.
 *
 * A CREDIT_CARD in PAYBACK_MODE does: its charges are sortable spending, and its statement credit is what
 * a checking-side payment gets matched against.
 *
 * `aggregator_source` is deliberately not a parameter — manual vs aggregator decides who WRITES the
 * transaction row (us, or the feed), never whether that row needs an Entry.
 *
 * Two asymmetries are load-bearing:
 *   - ON_BUDGET returns true for EVERY account type, not just ACCOUNT_TYPES_SUPPORTED_FOR_ON_BUDGET. Prod
 *     holds a handful of ON_BUDGET INVESTMENT/UNKNOWN accounts, created before `setType`/`_setStateCore`
 *     grew their type-vs-state guards (2026-02-23). They have entries today; silently stopping them is not
 *     this function's job.
 *   - PAYBACK_MODE is a positive allowlist (=== "CREDIT_CARD"), NOT `!== "LOAN"`. As of 2026-07-10 the two
 *     are behaviorally identical: every live writer is guarded (`_setStateCore` validates state-vs-type on
 *     each `budget_account.type` write; `s_account.setType` is the only post-creation writer of
 *     `Account.type` and validates type-vs-state), so PAYBACK_MODE + a type outside {CREDIT_CARD, LOAN} is
 *     unreachable. The allowlist is deliberate **fail-closed defense-in-depth**: `updateManualAccount` wrote
 *     `type` with no validation at all until 2026-07-10, and a future writer could skip the guard again.
 *     Only a card has charges to sort, so an unrecognized type should mint nothing. It costs nothing.
 *
 * `setType` DOES permit a payback card → LOAN in place (both are in ACCOUNT_TYPES_SUPPORTED_FOR_PAYBACK).
 * That transition is by design, and PAYBACK_MODE + LOAN is exactly the state this predicate exists for.
 * (An aggregator sync never retypes: `aggregator.service.updateAccount` writes `type: a.right.type`.)
 */
export function accountTracksTxnEntries(p) {
    switch (p.state) {
        case "ON_BUDGET":
            return true;
        case "PAYBACK_MODE":
            return p.accountType === "CREDIT_CARD";
        case "OFF_BUDGET":
            return false;
    }
}
export function getInstitutionName(p) {
    var _a;
    return (_a = p.membershipWithInstitutionMeta.nickname) !== null && _a !== void 0 ? _a : p.membershipWithInstitutionMeta.InstitutionMeta.c_name;
}
/**
 * Returns true for memberships still awaiting completion of the AddBankFlow / onboarding-phase-4
 * finalize step — i.e. the user hasn't yet assigned the bank's accounts on-budget / off-budget
 * and (where applicable) allocated their balances to pouches.
 *
 * Single source of truth for the "pending bank" filter — used by `<AddBanks>`, `<ConfirmAccounts>`,
 * the standalone flow's SelectOnBudget/AssignPouches steps, the Banks-tab recovery banner, and any
 * other surface that wants to know which banks are still being set up. Centralizing the predicate
 * here prevents drift (one site forgetting `!archived_at`, another adding a new condition only
 * locally, etc.).
 */
export function isPendingMembership(m) {
    return !m.setup_complete && !m.archived_at;
}
/**
 * Account-level twin of `isPendingMembership` (T-186). Returns true while this specific account is
 * still awaiting the AddBankFlow / onboarding-phase-4 finalize step (`Account.setup_complete=false`).
 *
 * Why per-account and not just per-membership: a user has ONE shared MANUAL membership holding ALL of
 * their manual accounts. Adding a new manual account re-arms that membership to pending, so a purely
 * membership-scoped filter drags every existing manual account back into the setup flow. Setup surfaces
 * therefore gate on `isPendingMembership(m) && isAccountAwaitingSetup(a)`, so only the genuinely-new
 * (default-`false`) account is shown. Flipped to true by `finalizeBankSetup`; re-armed to false wherever
 * the membership is (reset paths). Centralized here so the predicate can't drift between consumers.
 */
export function isAccountAwaitingSetup(a) {
    return !a.setup_complete;
}
/**
 * The "pending bank" filter for user-facing affordances — `isPendingMembership` MINUS any
 * switch-in-progress temporary membership (US-11).
 *
 * An aggregator switch connects a temporary new membership (`setup_complete=false`) while the user
 * reviews/confirms the account mapping. That temp membership must NOT surface in the recovery
 * banner / `<AddBanks>` pending list until the switch resolves, or the user could be nudged to
 * "finish setting up" a bank that's mid-switch. The ids come from CoreData's
 * `openSwitchNewMembershipIds` (AggregatorSwitchPlan rows with status="open").
 *
 * This composes `isPendingMembership` rather than changing it — the base predicate stays a pure
 * `(setup_complete, archived_at)` check with no DB/CoreData coupling. Use THIS helper at every
 * pending-bank UI site so the exclusion can't drift between consumers.
 */
export function filterPendingMembershipsAwaitingSetup(memberships, openSwitchNewMembershipIds) {
    const openSwitch = new Set(openSwitchNewMembershipIds);
    return memberships.filter((m) => isPendingMembership(m) && !openSwitch.has(m.id));
}
/**
 * True if this membership is the TEMPORARY "new" membership of an in-progress aggregator switch or
 * reconnect — i.e. its id is in CoreData's `openSwitchNewMembershipIds` (the `new_membership_id` of an
 * `AggregatorSwitchPlan` with status="open").
 *
 * Such a membership is a live, `setup_complete=false` DUPLICATE connection to the very institution the
 * user is switching, "parked" for up to 14 days while the switch is resumable (US-11). Before resumable
 * switching it lived only for the seconds of the flow, so it was hidden solely from the pending-bank
 * affordances (`filterPendingMembershipsAwaitingSetup`). Parked long-term it must be hidden from EVERY
 * membership-consuming surface (the bank list, "unconnected banks" counts, disconnected/lapse filters,
 * debt/chart lists) or a phantom duplicate bank appears. Use this at every membership-LIST render;
 * pair it with `isParkedSwitchTempAccount` for the account-LIST/picker/aggregate surfaces (the temp
 * membership's accounts are duplicates of the live old-membership accounts, so counting them double-
 * counts balances / net worth).
 *
 * Deliberately does NOT change `isPendingMembership` (a pure `(setup_complete, archived_at)` check with
 * no CoreData coupling) — the switch-hide condition rides `openSwitchNewMembershipIds`, threaded in by
 * the caller, exactly like `filterPendingMembershipsAwaitingSetup`.
 */
export function isParkedSwitchTempMembership(m, openSwitchNewMembershipIds) {
    return openSwitchNewMembershipIds.includes(m.id);
}
/**
 * True if this account belongs to a parked switch temp membership (its `membership_id` is in
 * `openSwitchNewMembershipIds`). The account-level companion to `isParkedSwitchTempMembership`: a temp
 * membership's accounts are DUPLICATES of the live accounts on the old membership being switched, so any
 * account LIST / picker / balance-or-net-worth aggregate must exclude them or it double-counts and shows
 * a phantom duplicate account. Account→membership `.find()` lookups don't need this directly — they're
 * covered transitively once the account list feeding them is filtered.
 */
export function isParkedSwitchTempAccount(a, openSwitchNewMembershipIds) {
    return openSwitchNewMembershipIds.includes(a.membership_id);
}
export const SORTED_INTERVALS = [
    "WEEKLY",
    "EVERY_2_WEEKS",
    "TWICE_A_MONTH",
    "MONTHLY",
    "EVERY_2_MONTHS",
    "QUARTERLY",
    "EVERY_6_MONTHS",
    "YEARLY",
];
const _typecheck_that_SORTED_INTERVALS_is_exhaustive = true;
/** A comparator that helps sort intervals from shortest to longest. */
export function compareIntervals(a, b) {
    const aIdx = SORTED_INTERVALS.indexOf(a);
    const bIdx = SORTED_INTERVALS.indexOf(b);
    return aIdx === bIdx ? 0 : aIdx < bIdx ? -1 : 1;
}
export function getIntervalLabel(p) {
    switch (p.interval) {
        case "MONTHLY":
            return p.i18N.common.monthly;
        case "EVERY_2_MONTHS":
            return p.isShort ? p.i18N.translateLater("Every 2M") : p.i18N.common.everyOtherMonth;
        case "QUARTERLY":
            return p.i18N.common.quarterly;
        case "EVERY_6_MONTHS":
            return p.isShort ? p.i18N.translateLater("Every 6M") : p.i18N.common.every6Months;
        case "YEARLY":
            return p.i18N.common.yearly;
        case "WEEKLY":
            return p.i18N.common.weekly;
        case "EVERY_2_WEEKS":
            return p.isShort ? p.i18N.translateLater("Every 2W") : p.i18N.common.every2Weeks;
        case "TWICE_A_MONTH":
            return p.isShort ? p.i18N.translateLater("2X Per Month") : p.i18N.common.twiceAMonth;
    }
}
// TODO: move these date tools to sharedUniversalTools
/**
 * Converts a RooDate to the RooDate in the specified Timezone
 */
export function dateToDateInTimezone(p) {
    const dt = p.date instanceof RooDateTime ? p.date : RooDateTime.fromJSDate(p.date);
    return dt.setZone(p.timezone).toJSDate(); // this format string is equivalent to toISODate()
}
/**
 * Gives the date in which a given time falls (for a given timezone) as an ISO 8601 date (in
 * (yyyy-MM-dd format) (which doesn't state a timezone).
 * Using this function is preferred over calling RooDateTime.toISODate directly because it forces you
 * to specify a timezone.
 */
export function dateToISO8601(p) {
    const dt = p.time instanceof RooDateTime ? p.time : RooDateTime.fromJSDate(p.time);
    return dt.setZone(p.timezone).toFormat("yyyy-MM-dd"); // this format string is equivalent to toISODate()
}
/**
 * Gives the date in which the present moment falls (for a given timezone) as an ISO 8601 date (in
 * (yyyy-MM-dd format) (which doesn't state a timezone).
 * Using this function is preferred over calling RooDateTime.now().toISODate directly because it forces
 * you to specify a timezone.
 */
export function nowToISO8601(p) {
    return dateToISO8601(Object.assign(Object.assign({}, p), { time: RooDateTime.now() }));
}
/**
 * Gives a RooDateTime representing the start of the day identified by a given ISO
 * 8601 date (in yyyy-MM-dd format) in a given timezone.
 * Using this function is preferred over calling RooDateTime.fromISO because it forces you to specify
 * a timezone.
 */
export function iso8601ToDateTimeAtMidnight(p) {
    return RooDateTime.fromFormat(p.dateStr, "yyyy-MM-dd", { zone: p.timezone });
}
/**
 * Gives a RooDate representing the start of the day identified by a given ISO
 * 8601 date (in yyyy-MM-dd format) in a given timezone.
 * Using this function is preferred over calling RooDateTime.fromISO because it forces you to specify
 * a timezone.
 */
export function iso8601ToDateAtMidnight(p) {
    return iso8601ToDateTimeAtMidnight(p).toJSDate();
}
/** Returns first RooDate found by checking (in order) the times from entry (if 'daily cash'), transaction, lineItem, and entry.created_at. */
export function getDateForTransactionDetails(p) {
    if (objectTypeHasSuffix(p.entry, "_DAILY_CASH")) {
        return iso8601ToDateAtMidnight({
            dateStr: p.entry.release_date_iso_str,
            timezone: p.budgetTimezone,
        });
    }
    if (objectTypeHasInfix(p.entry, "_TXN_")) {
        return p.entry.transaction.c_date;
    }
    return p.entry.created_at;
}
/** Gives the date that should be reported as the time-of-transaction, e.g. sorting logs. */
export function getDateForKeyEntry(p) {
    switch (p.keyEntry.type) {
        case "PENDING_TXN_NEW":
        case "PENDING_TXN_AWAITPOST":
        case "PENDING_TXN_MATCH":
        case "FINAL_TXN_OFF_BUDGET":
        case "REPROCESS":
        case "DELETE":
            return p.keyEntry.transaction.c_date;
        case "BROKEN_MATCH":
            const d1 = p.keyEntry.otherHalf.transaction.c_date;
            const d2 = p.keyEntry.dominantHalf.transaction.c_date;
            return d1 < d2 ? d1 : d2;
        case "PENDING_GHOST":
            return p.keyEntry.created_at;
        case "PENDING_DAILY_CASH":
            return iso8601ToDateAtMidnight({
                dateStr: p.keyEntry.release_date_iso_str,
                timezone: p.budgetTimezone,
            });
    }
}
/** Gives the date that should be used for assessing (re)process order, e.g. sorting cards. */
export function getProcessDateForKeyEntry(p) {
    switch (p.keyEntry.type) {
        case "PENDING_GHOST":
        case "PENDING_TXN_MATCH":
        case "PENDING_TXN_AWAITPOST":
            return p.keyEntry.choice_expiration;
        case "BROKEN_MATCH":
        case "REPROCESS":
        case "DELETE":
        case "PENDING_DAILY_CASH":
        case "PENDING_TXN_NEW":
        case "FINAL_TXN_OFF_BUDGET":
            return getDateForKeyEntry(p);
    }
}
/**
 * - If amtIfIncludesPaybackLI is not set, checks whether there's only one lineItems, and returns
 *   it (as {assigned}) (or null, if there aren't exactly 1).
 * - If amtIfIncludesPaybackLI is set, checks whether there are two lineItems, figures out which
 *   corresponds to amtIfIncludesPaybackLI, and returns an object with the lineItems sorted (or
 *   null, if there aren't exactly 2).
 */
export function getSingleAssignment(lineItems, amtIfIncludesPaybackLI) {
    if (amtIfIncludesPaybackLI === undefined) {
        if (lineItems.length !== 1)
            return null;
        return { assigned: lineItems[0], payback: undefined };
    }
    else {
        if (lineItems.length !== 2)
            return null;
        const assignedLI = lineItems.find((x) => roundTo3Decimals(x.amount) === roundTo3Decimals(amtIfIncludesPaybackLI));
        const paybackLI = lineItems.find((x) => x.amount !== (assignedLI === null || assignedLI === void 0 ? void 0 : assignedLI.amount));
        if (!assignedLI || !paybackLI)
            return null;
        return { assigned: assignedLI, payback: paybackLI };
    }
}
/**
 * Gives the ActionCards that a a user should process (filtering out KeyEntries that don't need
 * processing, creating derived cards, etc).
 */
export function getCardsUnsorted(coreData, timeMs) {
    return [
        ...getKeyEntryCards(),
        ...get_ACCOUNT_WAS_CLOSED_cards(),
        ...get_PAYBACK_PAYMENT_REMINDER_cards(),
        ...get_BANK_DISCONNECT_REMINDER_cards(),
        ...get_GOAL_MET_CELEBRATION_cards(),
    ];
    function getKeyEntryCards() {
        const deleteAndReplaceActive = !!coreData.budgetWithUsers.config.deleteAndReplaceSettings;
        // remove no-processing-needed KeyEntries
        return coreData.keyEntries
            .filter((ke) => keyEntryNeedsCard(ke, coreData.accounts, deleteAndReplaceActive, timeMs))
            .map((ke) => (Object.assign(Object.assign({}, ke), { derived: false })));
    }
    function get_GOAL_MET_CELEBRATION_cards() {
        return coreData.budgetWithUsers.config.incomeSplitter.otherMonthly
            .filter((a) => !!a.goal && a.goal.needsCelebrationAck)
            .map((a) => ({
            derived: true,
            type: "GOAL_MET_CELEBRATION_CARD",
            pouchId: a.pouchId,
            id: `GOAL_MET_CELEBRATION_CARD_${a.pouchId}`,
            goalMet: a.goal,
            amountPerMonth: a.amountPerMonth,
        }));
    }
    function get_ACCOUNT_WAS_CLOSED_cards() {
        const idsClosedButOnBudgetOrInPaybackPlan = coreData.accounts
            .filter((a) => {
            var _a, _b;
            if (!a.c_closed && !a.removed_at)
                return false;
            if (a.state !== "OFF_BUDGET")
                return true;
            const isInPaybackPlan = (_b = (_a = coreData.budgetWithUsers.config.incomeSplitter.payback) === null || _a === void 0 ? void 0 : _a.accountsPrioritized.some((x) => x.accountId === a.id)) !== null && _b !== void 0 ? _b : false;
            if (isInPaybackPlan)
                return true;
            return false;
        })
            .map((a) => a.id);
        return idsClosedButOnBudgetOrInPaybackPlan.map((id) => ({
            derived: true,
            type: "ACCOUNT_WAS_CLOSED",
            account_id: id,
            id: `ACCOUNT_WAS_CLOSED_${id}`,
        }));
    }
    function get_PAYBACK_PAYMENT_REMINDER_cards() {
        const payback = coreData.budgetWithUsers.config.incomeSplitter.payback;
        // A reminder fires per payback account that has a reminderDay set (opt-in — an account left at
        // "No Reminder" never cards). The trigger is the same under every strategy; only the CONTENT
        // differs: Manual Priority / Avalanche / Snowball show the recommended payment amount, while No
        // Plan shows an amount-free "you decide how much to pay" message (the variant branches on the
        // `noPlan` flag the controller derives from the strategy).
        return ((payback === null || payback === void 0 ? void 0 : payback.accountsPrioritized.filter((a) => {
            const nextReminderData = getNextPaybackReminderCardDate({
                accountId: a.accountId,
                timezone: coreData.budgetWithUsers.timezone,
                paybackPlan: payback,
                paybackAcks: coreData.budgetWithUsers.notificationAcks.paybackPaymentReminders,
            });
            return nextReminderData && nextReminderData.valueOf() <= timeMs;
        }).map((a) => ({
            derived: true,
            type: "PAYBACK_PAYMENT_REMINDER",
            account_id: a.accountId,
            id: `PAYBACK_PAYMENT_REMINDER_${a.accountId}`,
        }))) || []);
    }
    function get_BANK_DISCONNECT_REMINDER_cards() {
        // A parked aggregator-switch temp membership is a duplicate of the bank being switched, and every
        // membership-list surface hides it (see isParkedSwitchTempMembership) — so it never gets a card either.
        return coreData.membershipsWithInstitutionMeta
            .filter((m) => isMembershipDisconectedAndNotArchived(m) &&
            !isParkedSwitchTempMembership(m, coreData.openSwitchNewMembershipIds) &&
            isBankDisconnectReminderActive({ membership: m, timeMs }))
            .map((m) => ({
            derived: true,
            type: "BANK_DISCONNECT_REMINDER",
            membership_id: m.id,
            id: `BANK_DISCONNECT_REMINDER_${m.id}`,
        }));
    }
}
/**
 * Whether a BANK_DISCONNECT_REMINDER card should SHOW for an (already-confirmed) disconnected membership,
 * judged only by its reminder columns:
 *   - dismissed ("don't remind me again")  → hidden, until syncMembership clears it on reconnect
 *   - snoozed until a future time           → hidden; shown again once that time arrives (=== now counts)
 *   - neither                               → shown
 * The disconnected / archived / parked-switch checks live in the caller (get_BANK_DISCONNECT_REMINDER_cards).
 */
export function isBankDisconnectReminderActive(p) {
    if (p.membership.disconnect_reminder_dismissed_at)
        return false;
    const snoozedUntil = p.membership.disconnect_reminder_snoozed_until;
    if (snoozedUntil && snoozedUntil.valueOf() > p.timeMs)
        return false;
    return true;
}
/**
 * The badge count for the "New transactions" push (the key-entries cron and its admin debug endpoint,
 * which must agree). It's every card EXCEPT the bank-disconnect reminder: a disconnected bank has no
 * transactions to process, and it would otherwise fire that push on every run for the whole (often
 * multi-day) disconnect. The Roo-tab badge deliberately does NOT use this — it counts the whole deck.
 */
export function countCardsForTransactionsPush(p) {
    return getCardsUnsorted(p.coreData, p.timeMs).filter((card) => card.type !== "BANK_DISCONNECT_REMINDER").length;
}
/**
 * Note: DELETE technically always need processing _eventually_, but this function returns false
 * if isDeleteInGracePeriod returns true.
 */
export function keyEntryNeedsCard(ke, accounts, deleteAndReplaceActive, timestampOverride) {
    if (ke.type === "FINAL_TXN_OFF_BUDGET") {
        // T-103: a removed-from-budget txn is a terminal key entry delivered ONLY for the "Removed from
        // Budget" reconcile bucket — it is NOT a sortable card. Without this explicit exclusion it would hit
        // the `return true` default below and re-appear as a Roo Money card (ACCEPTANCE criterion 10).
        return false;
    }
    else if (ke.type === "PENDING_GHOST" || ke.type === "PENDING_TXN_MATCH" || ke.type === "PENDING_TXN_AWAITPOST") {
        const now = timestampOverride ? new RooDate(timestampOverride) : new RooDate();
        if (ke.choice_expiration <= now)
            return true;
        if (ke.type === "PENDING_TXN_MATCH" && getReasonMatchIsImpossible(ke, accounts))
            return true;
        return false;
    }
    else if (ke.type === "DELETE" && deleteAndReplaceActive && ke.transaction.removed_at) {
        return !isDeleteInGracePeriod(ke.transaction.removed_at, timestampOverride);
    }
    else {
        return true;
    }
}
/**
 * Whether a deleted transaction is still in the grace period during which we suppress its
 * ActionCard, giving the delete-and-replace detection system time to find a replacement.
 *
 * @note Should only be used if BudgetConfig.deleteAndReplaceSettings is set.
 */
export function isDeleteInGracePeriod(removedAt, timestampOverride) {
    const nowMs = timestampOverride !== null && timestampOverride !== void 0 ? timestampOverride : RooDate.now();
    const hoursElapsed = (nowMs - removedAt.getTime()) / (1000 * 60 * 60);
    return hoursElapsed < HOURS_TO_WAIT_BEFORE_PROCESSING_DELETE;
}
/**
 * Why a PENDING_TXN_MATCH awaiting a SPECIFIC account can never resolve, or null if it still can.
 *
 * There used to be two additional "wrong sign" reasons, both asserting that a cross-mode pair had to run
 * on-budget(-) → PAYBACK(+).  Both are gone: borrowing FROM a payback account (a cash advance) is now a
 * legal pair in the opposite direction, so a payback charge awaiting an on-budget account and an
 * on-budget deposit awaiting a payback account are each one half of a real transfer.  With both
 * directions legal, and same-mode pairs already constrained by the opposite-amount check in
 * getPotentialMatchType, NO sign combination is impossible — only a counterpart account that stopped
 * tracking transactions is.
 */
export function getReasonMatchIsImpossible(ke, accounts) {
    var _a;
    if (ke.account_id) {
        const mateAcctState = (_a = accounts.find((x) => x.id === ke.account_id)) === null || _a === void 0 ? void 0 : _a.state;
        if (!mateAcctState || mateAcctState === "OFF_BUDGET")
            return "WAITING_FOR_NOT_TXN_TRACKING";
    }
    return null;
}
/**
 * If the given KEs can be matched, gives a (non-translated) representation of the match type,
 * else returns null.
 *
 * Note: Permits otherKE to be PENDING_TXN_NEW (not only PENDING_TXN_MATCH/PENDING_GHOST).  Note
 *       that suggest-sent-to-match relies on that.
 *       When that isn't desired (e.g. for show-potential-matches UI), one must pre-filter the
 *       PENDING_TXN_NEW out separately.
 */
export function getPotentialMatchType(whatToMatch, otherKE, accounts) {
    var _a, _b;
    // Entry type check
    if (otherKE.id === whatToMatch.id)
        return null; // no self-matching
    if (otherKE.type === "DELETE")
        return null;
    if (otherKE.type !== "PENDING_TXN_NEW" &&
        otherKE.type !== "PENDING_GHOST" &&
        !isKEForEntryType(otherKE, "PENDING_TXN_MATCH"))
        return null;
    // Wait-for-specific-account check
    if (otherKE.type === "PENDING_TXN_MATCH" &&
        otherKE.account_id &&
        whatToMatch.transaction.account_id !== otherKE.account_id)
        return null;
    // Amount-is-opposite check
    const otherKEAmt = otherKE.type === "PENDING_GHOST" ? _.sum(otherKE.lineItems.map((li) => li.amount)) : otherKE.transaction.c_amount;
    if (otherKEAmt !== -1 * whatToMatch.transaction.c_amount)
        return null;
    // Account state check (and match type determination)
    if (otherKE.type === "PENDING_GHOST") {
        // PENDING_GHOST doesn't care about account state (for now)
        return "REPLACEMANUALTXN";
    }
    else {
        if (whatToMatch.transaction.account_id === otherKE.transaction.account_id)
            return "REFUND";
        const thisAcctState = (_a = accounts.find((x) => x.id === whatToMatch.transaction.account_id)) === null || _a === void 0 ? void 0 : _a.state;
        const otherAcctState = (_b = accounts.find((x) => x.id === otherKE.transaction.account_id)) === null || _b === void 0 ? void 0 : _b.state;
        if (!thisAcctState || thisAcctState === "OFF_BUDGET")
            return null; // shouldn't happen
        if (!otherAcctState || otherAcctState === "OFF_BUDGET")
            return null; // shouldn't happen
        // Both contributing (ON_BUDGET) → TRANSFER; both PAYBACK → TRANSFER.
        if (isContributingAccountState(thisAcctState) && isContributingAccountState(otherAcctState))
            return "TRANSFER";
        else if (thisAcctState === "PAYBACK_MODE" && otherAcctState === "PAYBACK_MODE")
            return "TRANSFER";
        else {
            // Account modes differ, so this is a HALFPAYBACK pair. WHICH one is decided purely by the sign of
            // the ON_BUDGET half — both directions are legal:
            //   negative on-budget half → the user PAID the debt down    (FINAL_TXN_HALFPAYBACK_PAYMENT)
            //   positive on-budget half → the user BORROWED from it      (FINAL_TXN_HALFPAYBACK_ASSIGNED),
            //                                                             i.e. a credit-card cash advance.
            // The amounts are already known to be exact opposites (checked above), so whichever leg the caller
            // happens to hold determines the other's sign — no need to look the counterpart's amount up.
            const onBudgetHalfAmount = isContributingAccountState(thisAcctState)
                ? whatToMatch.transaction.c_amount
                : -whatToMatch.transaction.c_amount;
            // A $0 pair carries no direction and moves no money; refuse it rather than pick a side. (The old
            // >=0 / <=0 guards excluded it too, so this preserves that behavior.)
            if (onBudgetHalfAmount === 0)
                return null;
            return onBudgetHalfAmount < 0 ? "CC_PAYMENT" : "CC_ADVANCE";
        }
    }
}
export function getUnprocessedAmtFromKEForAcct(accountId, ke) {
    if (ke.type === "REPROCESS" || ke.type === "DELETE") {
        if (ke.transaction.account_id !== accountId)
            return 0;
        return getUnprocessedAmtFromEntry(Object.assign(Object.assign({}, ke), { type: ke.prev_type }));
    }
    else if (ke.type === "BROKEN_MATCH") {
        const half1 = (function () {
            if (ke.dominantHalf.transaction.account_id !== accountId)
                return 0;
            return getUnprocessedAmtFromEntry(Object.assign(Object.assign({}, ke), { type: ke.prev_type, transaction: ke.dominantHalf.transaction, last_seen_amount: ke.last_seen_amount }));
        })();
        const half2 = (function () {
            if (ke.otherHalf.transaction.account_id !== accountId)
                return 0;
            return getUnprocessedAmtFromEntry(Object.assign(Object.assign({}, ke), { type: ke.prev_type, transaction: ke.otherHalf.transaction, last_seen_amount: -ke.last_seen_amount }));
        })();
        return half1 + half2;
    }
    else if (!narrowingStringIncludes(ke.type, "_TXN_")) {
        // "unprocessed amount" only applies to transaction types
        return 0;
    }
    else {
        // Not bothering to use getUnprocessedAmtFromEntry here, because it's not worth fighting the
        // type system over last_seen_amount.  All remaining types have
        // isOnBudgetTxn()==="either", so we copy the math from getUnprocessedAmtFromEntry for
        // that case.
        switch (ke.type) {
            case "PENDING_TXN_NEW":
            case "PENDING_TXN_MATCH":
            case "PENDING_TXN_AWAITPOST":
            // T-103: a removed-from-budget txn is money in the account (still counted in balance_total) that
            // sits in NO pouch — i.e. "unprocessed" for the pouches-vs-account reconcile, exactly like a
            // PENDING_TXN_NEW. The dedicated "Removed from Budget" bucket is what surfaces it on the LHS.
            case "FINAL_TXN_OFF_BUDGET":
                return ke.transaction.removed_at ? 0 : ke.transaction.c_amount;
        }
    }
}
/** Note: For any PENDING_ type, considers the full amount to be unprocessed (even for PENDING_TXN_MATCH). */
export function getUnprocessedAmtFromEntry(entry) {
    const newAmt = entry.transaction.removed_at ? 0 : entry.transaction.c_amount;
    const last_seen_amount = entry.last_seen_amount;
    switch (isOnBudgetTxn(entry.type)) {
        case "0":
            return 0;
        case "either":
            return newAmt;
        case "no":
        case "yes":
        case "+":
        case "-":
            return newAmt - last_seen_amount;
    }
}
/**
 * Indicates the state that the Account must have if the Entry is to be altered without creating a
 * new FINAL_ACCOUNT_ADDTXNS/FINAL_ACCOUNT_FREETXNS to compensate.
 * @returns undefined if any account type is permissible, else the required account type
 * @note If last_seen_amount === 0, this is always undefined (there's nothing to add/remove from
 *        budget when taking the txn into or out of "contributing" mode).
 */
export function getRequiredAccountMode(entry) {
    if (!entry.last_seen_amount)
        return undefined;
    switch (isOnBudgetTxn(entry.type)) {
        case "0":
        case "either":
            return undefined;
        case "no":
            return "PAYBACK_MODE";
        case "yes":
            return "ON_BUDGET";
        case "+":
            return entry.last_seen_amount > 0 ? "ON_BUDGET" : "PAYBACK_MODE";
        case "-":
            return entry.last_seen_amount < 0 ? "ON_BUDGET" : "PAYBACK_MODE";
    }
}
/** Collapses a transaction-tracking AccountState into the two-valued AccountTxnMode, or undefined if not txn-tracking. */
export function toAccountTxnMode(state) {
    if (isContributingAccountState(state))
        return "ON_BUDGET";
    if (state === "PAYBACK_MODE")
        return "PAYBACK_MODE";
    return undefined;
}
const _typecheck_TxnEntryAmtArgs = true;
/**
 * Indicates whether an Entry type is suitable for different account txn modes (PAYBACK or ON_BUDGET-like).
 * @returns - "no" if this is suitable for PAYBACK only
 *          - "yes" if this is suitable for ON_BUDGET (contributing) only
 *          - "either" if this is suitable for both and state can change with BudgetAccount.type (can only happen if there are no LineItems)
 *          - "+" if the last_seen_amount>0 half is for ON_BUDGET only (and the other half, if present, is for PAYBACK only)
 *          - "-" if the last_seen_amount<0 half is for ON_BUDGET only (and the other half, if present, is for PAYBACK only)
 *          - "0" for FINAL_TXN_IGNORE, which is like "either" except it is NOT appropriate for counting towards derived balance (should treat a 0 regardless of Transaction.c_amount)
 * @note behavioral effects are summarized in this table:
 * ```
 * | type | counts towards derived balance | needs FINAL_ACCOUNT_ADDTXNS to adjust while ON_BUDGET | needs FINAL_ACCOUNT_FREETXNS to adjust while non-ON_BUDGET |
 * | ------ | --- | --- | --- |
 * | no     | N   | Y   | N   |
 * | yes    | Y   | N   | Y   |
 * | either | Y   | N   | N   |
 * | 0      | N   | N   | N   |
 * | +      | if+ | if- | if+ |
 * | -      | if- | if+ | if- |
 * ```
 */
export function isOnBudgetTxn(type) {
    // Note: "+" is used only by FINAL_TXN_HALFPAYBACK_ASSIGNED (money borrowed from a PAYBACK account
    // -- a credit-card cash advance), the mirror of FINAL_TXN_HALFPAYBACK_PAYMENT's "-".  The arm was
    // reserved for exactly this before the type existed.  A "FINAL_TXN_HALFPAYBACK_INCOME" (borrowed
    // money routed through the income splitter rather than assigned to pouches) would be the other "+"
    // candidate; it is deliberately not built, since treating borrowed money as income inflates the
    // plan's sense of monthly income and skews every downstream projection.
    switch (type) {
        case "FINAL_TXN_IGNORE":
            return "0";
        case "PENDING_TXN_AWAITPOST":
        case "PENDING_TXN_MATCH":
        case "PENDING_TXN_NEW":
        case "FINAL_TXN_DELETE": // note that these always have last_seen_amount=0
        // T-103: an off-budget txn has no LineItems, still counts towards the derived balance (it is NOT
        // FINAL_TXN_IGNORE), and is valid in either account mode — exactly the "either" contract.
        case "FINAL_TXN_OFF_BUDGET":
            return "either";
        case "FINAL_TXN_MATCH":
        case "FINAL_TXN_ASSIGNED_INCOME":
        case "FINAL_TXN_ASSIGNED_NORMAL":
            return "yes";
        case "FINAL_TXN_PAYBACK_IGNORE":
        case "FINAL_TXN_PAYBACK_INCOME":
        case "FINAL_TXN_PAYBACK_ASSIGNED":
        case "FINAL_TXN_PAYBACK_MATCH":
            return "no";
        case "FINAL_TXN_HALFPAYBACK_PAYMENT":
            return "-";
        case "FINAL_TXN_HALFPAYBACK_ASSIGNED":
            return "+";
    }
}
/**
 * For a two-halves-with-opposite-modes Entry type (the FINAL_TXN_HALFPAYBACK_* family), says whether
 * THIS half is the ON_BUDGET one.
 *
 * Which sign is on-budget is a property of the TYPE, never a fixed rule: a payment's on-budget half is
 * the negative one, an advance's (cash advance) is the positive one.  Everything that has to pick a
 * half -- the narrower, the derived-balance SQL, the processors -- must agree, so they all derive it
 * here from `isOnBudgetTxn` rather than hand-writing a comparison.
 *
 * @returns false for a zero amount (neither half), which is not a legal state for these types.
 */
export function isOnBudgetHalfOfMatchPair(
/**
 * Narrowed to the HALFPAYBACK family deliberately.  The body falls back to `lastSeenAmount < 0`
 * for any type whose isOnBudgetTxn isn't "+", so a wider parameter would answer confidently (and
 * meaninglessly) for types that have no two-halves structure at all -- e.g. it would call a -$50
 * FINAL_TXN_ASSIGNED_NORMAL "the on-budget half".  Keep this narrow: a fourth call site passing
 * the wrong type should be a compile error, not a silently plausible boolean.
 */
type, lastSeenAmount) {
    return isOnBudgetTxn(type) === "+" ? lastSeenAmount > 0 : lastSeenAmount < 0;
}
export function actionIsApplicableToKeyEntry(actionType, keyEntry, accounts) {
    var _a, _b, _c, _d, _e, _f, _g;
    switch (actionType) {
        case "assignNormal":
        case "ignoreDuplicate":
        case "waitForMatch":
            return keyEntry.type === "PENDING_TXN_NEW";
        case "waitForPosted":
            return keyEntry.type === "PENDING_TXN_NEW" && keyEntry.transaction.c_status === "PENDING";
        case "incomeSplit":
            return keyEntry.type === "PENDING_TXN_NEW" && keyEntry.transaction.c_amount > 0;
        case "completeMatch":
            if (keyEntry.type !== "PENDING_TXN_NEW" && keyEntry.type !== "PENDING_TXN_MATCH")
                return false;
            return true; // this is all we can know until we know which account's transaction it is going to match
        case "paybackPayment":
            if (keyEntry.type !== "PENDING_TXN_NEW" && keyEntry.type !== "PENDING_TXN_MATCH")
                return false;
            if (keyEntry.transaction.c_amount === 0) {
                return false;
            }
            else if (keyEntry.transaction.c_amount < 0) {
                return isContributingAccountState((_a = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _a === void 0 ? void 0 : _a.state);
            }
            else {
                return ((_b = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _b === void 0 ? void 0 : _b.state) === "PAYBACK_MODE";
            }
        case "paybackAdvance":
            // The exact mirror of paybackPayment with the signs swapped: a BORROW's on-budget half is the
            // positive one (the deposit) and its payback half is the negative one (the card charge).
            if (keyEntry.type !== "PENDING_TXN_NEW" && keyEntry.type !== "PENDING_TXN_MATCH")
                return false;
            if (keyEntry.transaction.c_amount === 0) {
                return false;
            }
            else if (keyEntry.transaction.c_amount > 0) {
                return isContributingAccountState((_c = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _c === void 0 ? void 0 : _c.state);
            }
            else {
                return ((_d = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _d === void 0 ? void 0 : _d.state) === "PAYBACK_MODE";
            }
        case "adjustExpiration":
            return "choice_expiration" in keyEntry;
        case "ignoreDebt":
            if (keyEntry.type !== "PENDING_TXN_NEW")
                return false;
            return ((_e = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _e === void 0 ? void 0 : _e.state) === "PAYBACK_MODE";
        case "ackDelete":
            return keyEntry.type === "DELETE" && keyEntry.transaction.removed_at !== null;
        case "unmatch":
            return keyEntry.type === "BROKEN_MATCH";
        case "resetToNew":
            return (keyEntry.type === "PENDING_TXN_AWAITPOST" ||
                keyEntry.type === "PENDING_TXN_MATCH" ||
                keyEntry.type === "REPROCESS");
        case "autoReprocess":
            if (keyEntry.type === "REPROCESS") {
                return singleIsAutoReprocessable(keyEntry, accounts);
            }
            else if (keyEntry.type === "BROKEN_MATCH") {
                const accountState1 = (_f = accounts.find((x) => x.id === keyEntry.dominantHalf.transaction.account_id)) === null || _f === void 0 ? void 0 : _f.state;
                const accountState2 = (_g = accounts.find((x) => x.id === keyEntry.otherHalf.transaction.account_id)) === null || _g === void 0 ? void 0 : _g.state;
                if (!accountState1 || !accountState2)
                    return false;
                return matchIsAutoReprocessable(keyEntry, accountState1, accountState2);
            }
            else {
                return false;
            }
        case "processDailyCash":
            return keyEntry.type === "PENDING_DAILY_CASH";
        case "removeGhost":
            return keyEntry.type === "PENDING_GHOST";
    }
}
function singleIsAutoReprocessable(keyEntry, accounts) {
    var _a;
    const accountState = (_a = accounts.find((x) => x.id === keyEntry.transaction.account_id)) === null || _a === void 0 ? void 0 : _a.state;
    if (!accountState)
        return false;
    if (keyEntry.transaction.removed_at !== null)
        return false;
    switch (keyEntry.prev_type) {
        case "FINAL_TXN_DELETE":
            return false; // can't auto-reprocess a reappeared formerly-deleted
        case "FINAL_TXN_ASSIGNED_INCOME":
        case "FINAL_TXN_PAYBACK_INCOME":
            return false; // can't auto-reprocess income (we COULD, but we want the user to interact and possibly tweak it)
        case "PENDING_TXN_MATCH":
            return getReasonMatchIsImpossible(keyEntry, accounts) !== null;
        case "FINAL_TXN_IGNORE":
            return true; // always auto-reprocessable
        case "FINAL_TXN_ASSIGNED_NORMAL":
            // can't auto-reprocess a manual split; must resetToNew
            return keyEntry.lineItems.length === 1 && isContributingAccountState(accountState);
        case "FINAL_TXN_PAYBACK_ASSIGNED":
            // can't auto-reprocess a manual split; must resetToNew
            return keyEntry.lineItems.length === 2 && accountState === "PAYBACK_MODE";
        case "FINAL_TXN_HALFPAYBACK_PAYMENT":
            return keyEntry.transaction.c_amount < 0 && isContributingAccountState(accountState);
        case "FINAL_TXN_PAYBACK_IGNORE":
            return accountState === "PAYBACK_MODE";
    }
}
function matchIsAutoReprocessable(keyEntry, dominantAccountState, nondominantAccountState) {
    // can't auto-reprocess a match unless both halves changed in lockstep
    if (keyEntry.otherHalf.transaction.removed_at)
        return false;
    if (keyEntry.dominantHalf.transaction.removed_at)
        return false;
    const amt1 = roundTo3Decimals(-1 * keyEntry.otherHalf.transaction.c_amount);
    const amt2 = roundTo3Decimals(keyEntry.dominantHalf.transaction.c_amount);
    if (amt1 !== amt2)
        return false;
    switch (keyEntry.prev_type) {
        case "FINAL_TXN_MATCH":
            return isContributingAccountState(dominantAccountState) && isContributingAccountState(nondominantAccountState);
        case "FINAL_TXN_PAYBACK_MATCH":
            return dominantAccountState === "PAYBACK_MODE" && nondominantAccountState === "PAYBACK_MODE";
        case "FINAL_TXN_HALFPAYBACK_PAYMENT":
            return (keyEntry.dominantHalf.transaction.c_amount < 0 &&
                isContributingAccountState(dominantAccountState) &&
                nondominantAccountState === "PAYBACK_MODE");
        case "FINAL_TXN_HALFPAYBACK_ASSIGNED":
            // Mirror of the payment: the dominant half is the ON_BUDGET one either way (see the
            // dominant-half rule on _KeyEntryBrokenMatch), but for a borrow it is the POSITIVE half.
            return (keyEntry.dominantHalf.transaction.c_amount > 0 &&
                isContributingAccountState(dominantAccountState) &&
                nondominantAccountState === "PAYBACK_MODE");
    }
}
const TYPES_WITH_REASSIGNABLE_LINE_ITEMS = [
    "FINAL_TXN_ASSIGNED_NORMAL",
    "FINAL_TXN_PAYBACK_ASSIGNED",
];
export function typeAllowsLineItemChanges(type) {
    return narrowingListIncludes(TYPES_WITH_REASSIGNABLE_LINE_ITEMS, type);
}
/** Indicates whether a given Entry type may be freely (at any time) deleted, reset, or neither. */
export function entryTypeResetability(type) {
    switch (type) {
        case "FINAL_BOUNCE":
        case "FINAL_DAILY_CASH":
        case "FINAL_MANUAL_INCOME":
        case "FINAL_ACCOUNT_ADJUSTMENT":
        case "PENDING_GHOST":
            return "delete";
        case "FINAL_TXN_ASSIGNED_NORMAL":
        case "FINAL_TXN_ASSIGNED_INCOME":
        case "PENDING_TXN_MATCH":
        case "FINAL_TXN_MATCH":
        case "FINAL_TXN_PAYBACK_ASSIGNED":
        case "FINAL_TXN_PAYBACK_INCOME":
        case "FINAL_TXN_PAYBACK_IGNORE":
        case "FINAL_TXN_PAYBACK_MATCH":
        case "FINAL_TXN_HALFPAYBACK_PAYMENT":
        case "FINAL_TXN_HALFPAYBACK_ASSIGNED":
        case "FINAL_TXN_IGNORE":
        case "PENDING_TXN_AWAITPOST":
            return "reset";
        case "FINAL_ACCOUNT_ARCHIVE":
        case "FINAL_ACCOUNT_INIT":
        case "FINAL_ACCOUNT_TO_PAYBACK":
        case "FINAL_ACCOUNT_ADDTXNS":
        case "FINAL_ACCOUNT_FREETXNS":
        case "FINAL_TXN_DELETE":
        case "PENDING_DAILY_CASH":
        case "PENDING_TXN_NEW":
        // T-103: off-budget is a terminal state; its re-add is the dedicated addTransactionBackToBudget
        // endpoint, not a generic reset/delete.
        case "FINAL_TXN_OFF_BUDGET":
            return "neither";
    }
}
export function isRealUserId(userId) {
    return !userId.includes("USER_ID_");
}
export function entryHasUserNotes(entry) {
    return !!entry.note || entry.image_url !== null;
}
export function isKEForEntryType(ke, entryType) {
    if (ke.type === "REPROCESS" || ke.type == "DELETE" || ke.type === "BROKEN_MATCH") {
        return ke.prev_type === entryType;
    }
    else {
        return ke.type === entryType;
    }
}
export function getOptionalKeyFieldsFromNarrowedEntry(
/** Typically this is a whole NarrowedEntryOrTxn, but for ease of use in texts we slim it to just what is needed */
narrowedEntry) {
    const transaction = "transaction" in narrowedEntry ? narrowedEntry.transaction : undefined;
    // Note: we can't just do `"lineItems" in matchingEntry` because typescript won't narrow it well enough to know lineItems's type.
    const matchingLineItems = "onBudgetHalf" in narrowedEntry && !narrowedEntry.onBudgetHalf ? narrowedEntry.matchingEntry.lineItems : [];
    const lineItemsFromThis = "lineItems" in narrowedEntry ? narrowedEntry.lineItems : [];
    return {
        transaction,
        lineItemsIncludingOtherHalf: [...lineItemsFromThis, ...matchingLineItems],
        owningAccountId: objectTypeHasPrefix(narrowedEntry, "FINAL_ACCOUNT_")
            ? narrowedEntry.account_id
            : transaction === null || transaction === void 0 ? void 0 : transaction.account_id,
        matchingEntry: "matchingEntry" in narrowedEntry ? narrowedEntry.matchingEntry : undefined,
        choiceExpiration: "choice_expiration" in narrowedEntry ? narrowedEntry.choice_expiration : undefined,
    };
}
export function isMembershipDisconectedAndNotArchived(m) {
    return !m.c_aggregator_connected && !m.archived_at;
}
/**
 * The "this bank was disconnected because the subscription LAPSED" filter (billing #4 → #5). Both markers
 * are set together by the lapse-disconnect (`archiveMembershipForLapse`): `archived_at` (the bank is gone)
 * AND `lapse_disconnected_at` (it was lapse-caused, not a user-initiated remove). This is what the
 * post-resubscribe "reconnect your banks" entry surface targets — distinct from a user remove (which has
 * `archived_at` but NOT `lapse_disconnected_at`, and must NOT be offered budget-restoring reconnect) and from
 * `isMembershipDisconectedAndNotArchived` (a healthy-but-lost connection, still `archived_at == null`).
 */
export function isLapseArchivedMembership(m) {
    return !!m.archived_at && !!m.lapse_disconnected_at;
}
export function convertDateToMonthStrForIncome(date) {
    return convertMonthAndYearToMonthStrForIncome(date.getMonth(), date.getFullYear());
}
/** @param month in [0,11] range */
export function convertMonthAndYearToMonthStrForIncome(month, year) {
    return `${year}-${String(month + 1).padStart(2, "0")}`;
}
export function convertDatePickerSelectionsToMonthAndYearValues(selections, startDate) {
    if (selections.type === "single") {
        return monthsForSelection(selections.selection, startDate);
    }
    else {
        return Object.keys(selections.selections).reduce((acc, year) => {
            var _a;
            const yearNum = parseInt(year);
            Object.keys((_a = selections.selections[yearNum]) !== null && _a !== void 0 ? _a : {}).forEach((month) => {
                acc.push({ month: parseInt(month), year: yearNum });
            });
            return acc;
        }, []);
    }
    return [];
}
export function monthsForSelection(key, budgetStartDate) {
    const now = new RooDate();
    const startOfMonth = (d) => new RooDate(d.getFullYear(), d.getMonth(), 1);
    const clampStart = startOfMonth(budgetStartDate);
    let end = startOfMonth(now);
    if (end < clampStart)
        end = clampStart;
    const spanMonths = (from, to) => {
        const out = [];
        let y = from.getFullYear();
        let m = from.getMonth() + 1; // 1–12
        const endY = to.getFullYear();
        const endM = to.getMonth() + 1;
        while (y < endY || (y === endY && m <= endM)) {
            out.push({ month: m, year: y });
            m++;
            if (m === 13) {
                m = 1;
                y++;
            }
        }
        return out;
    };
    const backN = (n) => {
        let y = end.getFullYear();
        let m = end.getMonth() + 1;
        const out = [];
        for (let i = 0; i < n; i++) {
            const d = new RooDate(y, m - 1, 1);
            if (d < clampStart)
                break;
            out.push({ month: m, year: y });
            m--;
            if (m === 0) {
                m = 12;
                y--;
            }
        }
        return out.reverse();
    };
    switch (key) {
        case "1M":
            return backN(1);
        case "3M":
            return backN(3);
        case "6M":
            return backN(6);
        case "12M":
            return backN(12);
        case "ytd": {
            const jan1 = new RooDate(end.getFullYear(), 0, 1);
            const from = jan1 < clampStart ? clampStart : jan1;
            return spanMonths(from, end);
        }
        case "all":
        default:
            return spanMonths(clampStart, end);
    }
}
/**
 * Determines which pouches to fund for a given account, given alreadyAssignedByPouchId.
 * Also modifies alreadyAssignedByPouchId.
 */
export function determineAccountAllotment(p) {
    var _a;
    const balanceDestinations = [];
    let amountLeftToFund = p.accountBalance;
    for (const pouchWithSeedData of p.pouchesWithSeedData) {
        const prevAssignedToThisPouch = (_a = p.alreadyAssignedByPouchId[pouchWithSeedData.pouchId]) !== null && _a !== void 0 ? _a : 0;
        let remainingAmountToFundThisPouch = roundTo3Decimals(pouchWithSeedData.initialBalance - prevAssignedToThisPouch);
        if (remainingAmountToFundThisPouch) {
            const amountToFund = p.totalAmountSign === "positive"
                ? Math.min(remainingAmountToFundThisPouch, amountLeftToFund)
                : Math.max(remainingAmountToFundThisPouch, amountLeftToFund);
            p.alreadyAssignedByPouchId[pouchWithSeedData.pouchId] = roundTo3Decimals(prevAssignedToThisPouch + amountToFund);
            balanceDestinations.push({
                amount: amountToFund,
                pouch_id: pouchWithSeedData.pouchId,
            });
            amountLeftToFund = roundTo3Decimals(amountLeftToFund - amountToFund);
            if (roundTo3Decimals(amountLeftToFund) === 0) {
                break;
            }
        }
    }
    return balanceDestinations;
}
export function createPouchAssignmentsForBulkAccountContributionChange(p) {
    const totalToAdd = roundTo3Decimals(p.allAccountsToAdd.reduce((total, x) => total + x.amt, 0));
    const totalToAdd2 = roundTo3Decimals(Object.values(p.pouchAssignments).reduce((total, x) => total + x, 0));
    if (totalToAdd2 !== totalToAdd) {
        s_log.getSharedLogger().warn({
            message: "Error in T.R.createPouchAssignmentsForBulkAccountContributionChange() - input amount mismatch",
            lid: "r-oB7H8aWy",
            meta: p,
        });
        return { status: "error" };
    }
    const sign = totalToAdd >= 0 ? "positive" : "negative";
    const zeroBalanceAccounts = p.allAccountsToAdd.filter((acc) => acc.amt === 0);
    const accountIdToPouchAssignmentMapping = zeroBalanceAccounts.reduce((acc, val) => {
        acc[val.id] = [];
        return acc;
    }, {});
    const nonZeroBalanceAccounts = p.allAccountsToAdd.filter((acc) => acc.amt !== 0);
    const pouchesWithAssignedAmountBiggestToSmallestMatchingSign = _.orderBy(Object.keys(p.pouchAssignments).map((pouchId) => {
        return { pouchId, amount: p.pouchAssignments[pouchId] };
    }), (a) => a.amount, sign === "negative" ? "asc" : "desc");
    const accountsWithSameSignAsTotalBiggestToSmallestMatchingSign = _.orderBy(nonZeroBalanceAccounts.filter((acc) => Math.sign(acc.amt) === Math.sign(totalToAdd)), (a) => a.amt, sign === "negative" ? "asc" : "desc");
    const accountsWithDifferentSignAsTotal = nonZeroBalanceAccounts.filter((acc) => Math.sign(acc.amt) !== Math.sign(totalToAdd));
    const totalAmountOfAccountBalancesThatAreOppositeSignOfTotal = _.sum(accountsWithDifferentSignAsTotal.map((a) => a.amt));
    if (pouchesWithAssignedAmountBiggestToSmallestMatchingSign.length) {
        const firstPouchIdToAssignTo = pouchesWithAssignedAmountBiggestToSmallestMatchingSign[pouchesWithAssignedAmountBiggestToSmallestMatchingSign.length - 1].pouchId;
        let assignedAmountsByPouchId = {
            [firstPouchIdToAssignTo]: totalAmountOfAccountBalancesThatAreOppositeSignOfTotal,
        };
        accountsWithDifferentSignAsTotal.forEach((acc) => {
            accountIdToPouchAssignmentMapping[acc.id] = [
                {
                    pouch_id: firstPouchIdToAssignTo,
                    amount: acc.amt,
                },
            ];
        });
        for (const account of accountsWithSameSignAsTotalBiggestToSmallestMatchingSign) {
            const balanceDestinations = determineAccountAllotment({
                accountBalance: account.amt,
                pouchesWithSeedData: pouchesWithAssignedAmountBiggestToSmallestMatchingSign.map((a) => {
                    return {
                        initialBalance: a.amount,
                        pouchId: a.pouchId,
                    };
                }),
                alreadyAssignedByPouchId: assignedAmountsByPouchId,
                totalAmountSign: sign,
            });
            accountIdToPouchAssignmentMapping[account.id] = balanceDestinations;
        }
    }
    const pouchesTotalRaw = _(accountIdToPouchAssignmentMapping)
        .values()
        .flatten()
        .sumBy((a) => a.amount);
    const pouchesTotal = roundTo3Decimals(pouchesTotalRaw);
    if (pouchesTotal !== totalToAdd) {
        s_log.getSharedLogger().warn({
            message: "Error in T.R.createPouchAssignmentsForBulkAccountContributionChange() - pouch amount mismatch",
            lid: "r-JsQP1lam",
            meta: Object.assign(Object.assign({}, p), { pouchesTotal, accountIdToPouchAssignmentMapping }),
        });
        return { status: "error" };
    }
    return { status: "success", accountIdToPouchAssignmentMapping };
}
export function getAccountStateChangeTarget(p) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        // ON_BUDGET is the only "contributing" state; it and PAYBACK are the transaction-tracking states.
        const wasContributing = p.currentState.state === "ON_BUDGET";
        const wasTxnTracking = wasContributing || p.currentState.state === "PAYBACK_MODE";
        switch (p.newState) {
            case "ON_BUDGET":
                // handle the cases where we use existing BudgetAccount.start_date rather than (re)setting it.
                // In these cases, we don't include contributionAdjustmentForTxnCutoff even if it's nonzero,
                // because presumably the derived balance was ok when BudgetAccount.start_date was first set.
                // TODO: Instead of checking !txnCutoffDateOverride, consider checking `txnCutoffDate ===
                //       current start_date` and making txnCutoffDate required.
                // Re-saving an already-ON_BUDGET account with no cutoff change leaves the derived balance
                // untouched, so no FINAL_ACCOUNT_INIT is needed.
                if (wasContributing && !p.txnCutoffDateOverride) {
                    return null;
                }
                if (p.currentState.state === "PAYBACK_MODE" && !p.txnCutoffDateOverride) {
                    return {
                        entryType: "FINAL_ACCOUNT_INIT",
                        targetAmount: p.currentState.amtWouldAddToBudgetIfNoAdjustmentForCutoffDate,
                    };
                }
                // handle all other cases, where contributionAdjustmentForTxnCutoff is needed.
                const txnCutoffDate = (_a = p.txnCutoffDateOverride) !== null && _a !== void 0 ? _a : new RooDate(); // isn't p.currentState.start_date.
                const contributionAdjustmentForTxnCutoff = yield p.serverApi.getContributionAdjustmentForTxnCutoff(txnCutoffDate);
                // inline state checks (not the wasContributing boolean) so TS narrows the else arm to the
                // non-contributing states that actually carry amtWouldAddToBudgetIfNoAdjustmentForCutoffDate.
                if (p.currentState.state === "ON_BUDGET") {
                    return { entryType: "FINAL_ACCOUNT_INIT", targetAmount: contributionAdjustmentForTxnCutoff };
                }
                else {
                    return {
                        entryType: "FINAL_ACCOUNT_INIT",
                        targetAmount: p.currentState.amtWouldAddToBudgetIfNoAdjustmentForCutoffDate + contributionAdjustmentForTxnCutoff,
                    };
                }
            case "PAYBACK_MODE":
                if (p.currentState.state === "PAYBACK_MODE" && !p.txnCutoffDateOverride)
                    return null;
                return {
                    entryType: "FINAL_ACCOUNT_TO_PAYBACK",
                    // inline state checks (not the wasContributing boolean) so TS narrows to the contributing arm
                    // that actually carries amtInPouches.
                    targetAmount: p.currentState.state === "ON_BUDGET" ? -p.currentState.amtInPouches : 0,
                };
            case "OFF_BUDGET":
                if (wasTxnTracking) {
                    return {
                        entryType: "FINAL_ACCOUNT_ARCHIVE",
                        targetAmount: p.currentState.state === "ON_BUDGET" ? -p.currentState.amtInPouches : 0,
                    };
                }
                else {
                    return null;
                }
        }
    });
}
// TODO: This is what getAccountStateChangeTarget probably always should have been (since it
//   already needs a serverApi, making it odd to also require AccountSummaryInfo separately).
//   Consolidate the two.
export function getAccountStateChangeTarget2(p) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const budgetId = p.budgetId;
        const accountId = p.accountId;
        const info = yield p.serverApi.getSummaryInfo({ budgetId, accountId });
        const targetEntry = yield getAccountStateChangeTarget({
            currentState: info,
            newState: p.targetState,
            txnCutoffDateOverride: p.txnCutoffDateOverride,
            serverApi: {
                getContributionAdjustmentForTxnCutoff: (txnCutoffDate) => {
                    return p.serverApi.getContributionAdjustmentForTxnCutoff({ budgetId, accountId, txnCutoffDate });
                },
            },
        });
        return (_a = targetEntry === null || targetEntry === void 0 ? void 0 : targetEntry.targetAmount) !== null && _a !== void 0 ? _a : 0;
    });
}
/** WARNING: This assumes that txn.source_item is MXTransaction | PlaidTransaction | QuilttTransaction.  If it holds other types, there MAY be false positives. */
export function isMXTransactionSrc(txn) {
    if (!txn.source_item || typeof txn.source_item !== "object")
        return false;
    const MX_ONLY_FIELDS = ["top_level_category", "is_bill_pay", "is_subscription", "is_recurring"]; // this list isn't complete
    return MX_ONLY_FIELDS.some((k) => k in txn.source_item);
}
/** WARNING: This assumes that txn.source_item is MXTransaction | PlaidTransaction | QuilttTransaction.  If it holds other types, there MAY be false positives. */
export function isPlaidTransactionSrc(txn) {
    if (!txn.source_item || typeof txn.source_item !== "object")
        return false;
    return "personal_finance_category" in txn.source_item;
}
/** WARNING: This assumes that txn.source_item is MXTransaction | PlaidTransaction | QuilttTransaction.  If it holds other types, there MAY be false positives. */
export function isQuilttTransactionSrc(txn) {
    if (!txn.source_item || typeof txn.source_item !== "object")
        return false;
    return "provider" in txn.source_item;
}
const stateOrder = ["ON_BUDGET", "PAYBACK_MODE", "OFF_BUDGET", "hidden"];
const stateRank = Object.fromEntries(stateOrder.map((t, i) => [t, i]));
export function sortAccountsByBehaviorTypeThenAlphabetical(accounts) {
    return _.orderBy(accounts, [
        (a) => stateRank[a.stateWithHidden],
        (a) => getAccountName({ account: a }).toLowerCase(),
    ], ["asc", "asc"]);
}
/**
 * Gets the identifier of the merchant involved in a transaction, if available.
 * Not intrinsically meaningful, but helpful for comparisons within a single aggregator (the same
 *   merchant may have a different ID for different data aggregators).
 * TODO: Consider adding this to Transaction in the DB rather than pulling it out of source_item
 */
export function getMerchantIdentifier(txn) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    if (isMXTransactionSrc(txn)) {
        return (_a = txn.source_item.merchant_guid) !== null && _a !== void 0 ? _a : null; // observed: merchant_category_code and merchant_location_guid seem to add little over this
    }
    else if (isPlaidTransactionSrc(txn)) {
        return (_b = txn.source_item.merchant_name) !== null && _b !== void 0 ? _b : null; // observed: merchant_entity_id was too often null to be as useful as this field is
    }
    else if (isQuilttTransactionSrc(txn)) {
        switch (txn.source_item.provider) {
            case "MX":
                return (_g = (_f = (_e = (_d = (_c = txn.source_item.remoteData) === null || _c === void 0 ? void 0 : _c.mx) === null || _d === void 0 ? void 0 : _d.transaction) === null || _e === void 0 ? void 0 : _e.response) === null || _f === void 0 ? void 0 : _f.merchantGuid) !== null && _g !== void 0 ? _g : null;
            case "PLAID":
                return (_m = (_l = (_k = (_j = (_h = txn.source_item.remoteData) === null || _h === void 0 ? void 0 : _h.plaid) === null || _j === void 0 ? void 0 : _j.transaction) === null || _k === void 0 ? void 0 : _k.response) === null || _l === void 0 ? void 0 : _l.merchantName) !== null && _m !== void 0 ? _m : null;
            case "FINICITY":
                const customerId = (_r = (_q = (_p = (_o = txn.source_item.remoteData) === null || _o === void 0 ? void 0 : _o.finicity) === null || _p === void 0 ? void 0 : _p.transaction) === null || _q === void 0 ? void 0 : _q.response) === null || _r === void 0 ? void 0 : _r.customerId;
                const normalizedPayeeName = (_w = (_v = (_u = (_t = (_s = txn.source_item.remoteData) === null || _s === void 0 ? void 0 : _s.finicity) === null || _t === void 0 ? void 0 : _t.transaction) === null || _u === void 0 ? void 0 : _u.response) === null || _v === void 0 ? void 0 : _v.categorization) === null || _w === void 0 ? void 0 : _w.normalizedPayeeName;
                return customerId ? `${customerId}` : normalizedPayeeName !== null && normalizedPayeeName !== void 0 ? normalizedPayeeName : null;
            case "AKOYA":
            case "DISCONNECTED":
            case "MOCK":
                return null; // not yet supported
        }
    }
    else {
        return null; // shouldn't happen
    }
}
//# sourceMappingURL=rooUniversalTools.js.map